import { useState } from "react";
import { Link } from "react-router-dom";
import { IconTrendingDown, IconTrendingUp } from "@tabler/icons-react";

import type { AffiliateProgramHub, ProgramDetails } from "@/services/program.service";
import { formatCurrency } from "@/utils/currency";
import { formatNumber } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { ChartToggle } from "@/components/dashboard/metric-cell";
import { MetricChart } from "@/components/dashboard/metric-chart";
import { CodeCell } from "./code-card";
import { describeChange, money } from "./helpers";

/** The chart needs at least this many non-zero days to say anything. */
const MIN_POINTS = 2;

function Change({ change }: { change: ReturnType<typeof describeChange> }) {
  const Icon =
    change.direction === "up" ? IconTrendingUp : change.direction === "down" ? IconTrendingDown : null;
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      {Icon && <Icon className="size-3.5 shrink-0" aria-hidden />}
      <span>{change.text}</span>
    </div>
  );
}

const cell = "flex min-w-0 flex-col gap-1 border-l border-t border-border px-5 py-4";

function Cell({ label, value, children }: { label: string; value: string; children?: React.ReactNode }) {
  return (
    <div className={cell}>
      <div className="text-[13px] font-medium text-foreground">{label}</div>
      <div className="truncate text-3xl font-semibold tabular-nums tracking-tight text-foreground">{value}</div>
      {children}
    </div>
  );
}

/**
 * The one number an affiliate is here for, what they share to grow it, and
 * the figures behind it. Each figure appears once: orders sit under the
 * average, the money waiting sits with its request link.
 */
export function EarningsCard({ hub, program }: { hub: AffiliateProgramHub; program: ProgramDetails }) {
  const { performance, earnings, standing } = hub;
  const days = performance.windowDays || 30;
  const { current, previous } = performance;
  const byDay = Object.fromEntries(performance.salesByDay.map((d) => [d.date, d.revenue]));
  const activeDays = performance.salesByDay.filter((d) => d.revenue > 0).length;
  const averageOrder = current.orders > 0 ? current.sales / current.orders : 0;
  const chartable = activeDays >= MIN_POINTS;
  const [chartOpen, setChartOpen] = useState(false);

  return (
    <DashboardCard
      id="dashboard-earnings-card"
      title={`Last ${days} days`}
      action={chartable ? <ChartToggle open={chartOpen} onToggle={() => setChartOpen((o) => !o)} /> : undefined}
    >
      <div className="-ml-px grid grid-cols-1 lg:grid-cols-12">
        <div className={cn(cell, "py-5 lg:col-span-5 lg:py-6")}>
          <div className="text-[13px] font-medium text-foreground">Earned</div>
          <div className="text-5xl font-semibold tabular-nums tracking-tight text-foreground">
            {money(current.commission)}
          </div>
          {current.orders === 0 && standing.totalOrders === 0 ? (
            <p className="text-sm text-muted-foreground">Your first order lands here the moment it happens.</p>
          ) : (
            <Change change={describeChange(current.commission, previous.commission, days, money)} />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:col-span-7">
          <CodeCell program={program} className={cell} />
          <Cell label="Sales" value={money(current.sales)}>
            <Change change={describeChange(current.sales, previous.sales, days, money)} />
          </Cell>
          <Cell label="Average order" value={money(averageOrder)}>
            <div className="text-xs text-muted-foreground">
              {current.orders === 0
                ? "No orders in this period"
                : `Across ${formatNumber(current.orders)} order${current.orders === 1 ? "" : "s"}`}
            </div>
          </Cell>
          <Cell label="Ready to request" value={money(earnings.available)}>
            {earnings.available > 0 ? (
              <Link
                to="/affiliate/payouts"
                className="w-fit text-xs font-medium text-brand-700 underline-offset-4 hover:underline"
              >
                Request payout
              </Link>
            ) : (
              <div className="text-xs text-muted-foreground">
                {earnings.requested > 0
                  ? `${money(earnings.requested)} requested, waiting on the brand`
                  : "Commission lands here as orders come in"}
              </div>
            )}
          </Cell>
        </div>
      </div>
      {chartOpen && chartable && (
        <MetricChart
          metric={{
            label: "Sales",
            value: money(current.sales),
            series: byDay,
            unit: "sale",
            format: formatCurrency,
          }}
        />
      )}
    </DashboardCard>
  );
}
